import { SEOHead } from '@/components/seo/SEOHead';
import { getAuthLink } from '@/lib/links';
import { getPlanSignupLink } from '@/lib/planSignupLink';
import { Button } from '@/components/ui/button';
import { ROICalculator } from '@/components/marketing';
import { useSanityPage } from '@/lib/sanity.hooks';
import { useTranslation } from '../hooks/useTranslation';
import { useState } from 'react';
import {
  Sparkles,
  ArrowRight,
  Zap,
  MessageSquare,
  Mail,
  Globe,
  BarChart3,
  TrendingUp,
  Activity,
  CircleDollarSign,
  Info,
} from 'lucide-react';
import {
  Users,
  Check,
  X,
  Phone,
  Sun,
  Crown,
  Shield,
  Star,
  Rocket,
  Lock,
  ListChecks,
  Receipt,
  CaretDown,
} from '@phosphor-icons/react';
import { SectionBadge } from '@/components/marketing';

// ─────────────────────────────────────────
// DATA — plan prices match the live Stripe catalog in
// workers/app-edge/src/lib/stripe-catalog.ts (0 / 79 / 99). Checkout bills Pro and
// Business per seat (#2646); annual prices are not created yet, and annual
// checkout fails closed until they are. The root content/pricing.json only
// feeds the legacy WordPress sync and is not read by this page.
// Starter $0 · Pro $79/user · Business $99/user
// Month-to-month · everything free during beta, 30% off for founding members
// ─────────────────────────────────────────

const basePlans = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 0,
    annualPrice: 0,
    period: '/month',
    description:
      'For solo roofers getting started. Everything you need to manage leads and close deals — free during beta.',
    isPopular: false,
    ctaText: 'Start Free',
    highlight: null as string | null,
    features: [
      'Up to 50 active leads',
      'Basic lead pipeline',
      'Instant estimates',
      'Invoice generation',
      'Mobile-friendly CRM',
      'Email support',
      '1 user included',
    ],
    missingFeatures: [
      'Proposals & e-signatures',
      'Territory & storm tracking',
      'Door-to-door canvassing',
      'Marketing automation',
      'API access',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 79,
    annualPrice: 63,
    period: '/user/month',
    description: 'For growing teams that need automation, proposals, and territory management.',
    isPopular: true,
    ctaText: 'Start Free',
    highlight: 'MOST POPULAR',
    features: [
      'Everything in Starter',
      'Unlimited leads',
      'Advanced sales pipeline',
      'Proposal builder with e-sign',
      'Contract management',
      'Territory & storm tracking',
      'Door-to-door canvassing',
      'Email & SMS campaigns (sending via Marketing Suite add-on)',
      'Team management (up to 10)',
      'Priority support',
    ],
    missingFeatures: [
      'AI lead scoring & insights',
      'Custom workflow automations',
      'API access & custom integrations',
      'Dedicated account manager',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    monthlyPrice: 99,
    annualPrice: 79,
    period: '/user/month',
    description:
      'For established companies that want AI insights, advanced analytics, and white-glove onboarding.',
    isPopular: false,
    ctaText: 'Start Free',
    highlight: 'BEST FOR TEAMS',
    features: [
      'Everything in Pro',
      'No user cap ($99 per active user)',
      'AI lead scoring & insights',
      'Advanced marketing automation',
      'Custom workflow automations',
      'Job costing & profitability',
      'Commission tracking',
      'Customer portal',
      'API access & custom integrations',
      'Dedicated account manager',
      'White-glove onboarding',
    ],
    missingFeatures: [] as string[],
  },
];

const addOnModules = [
  {
    id: 'marketing-bundle',
    category: 'ConnectIQ',
    icon: MessageSquare,
    name: 'Marketing Suite',
    tagline: 'Email + SMS outbound marketing with roofing-specific lead nurture AI',
    monthlyPrice: 59,
    annualPrice: 47,
    note: 'Email-only plan available at $29/mo',
    color: '#94a3b8',
    features: [
      '25,000 marketing emails/month',
      '1,000 SMS credits/month',
      'JourneyMaker automation builder (marketing flows)',
      'Proprietary lead nurture algorithm for roofers',
      'Segment targeting by state, stage, and reason code',
      'Broadcast campaigns (email + SMS)',
      'Delivery & response analytics',
      'Custom sending domain — free, no extra charge',
      'TCPA-compliant opt-in/opt-out management',
      'Additional emails: $1.50/1,000',
      'Additional SMS: $0.015/message',
    ],
    competitor:
      'Comparable stacks (ActiveCampaign + SlickText): $60–$130/mo for generic tools. Ours is roofing-native.',
  },
  {
    id: 'transactional',
    category: 'ConnectIQ',
    icon: Zap,
    name: 'Transactional Comms',
    tagline: 'Operational SMS & email triggered automatically by your workflow events',
    monthlyPrice: 15,
    annualPrice: 12,
    note: 'Email-only at $9/mo · SMS-only at $9/mo',
    color: '#cbd5e1',
    features: [
      '2,000 transactional emails/month',
      '500 transactional SMS/month',
      'JourneyMaker (operational triggers only)',
      'Lead source-based routing rules',
      'Custom sending domain — included',
      'New-lead auto-notify, appointment confirmations',
      'Invoice sent & payment reminder automations',
      'Configure exactly what fires and when',
      'Additional emails: $1.50/1,000',
      'Additional SMS: $0.015/message',
    ],
    competitor: 'Twilio (DIY): $20–$80/mo to set up + developer time. We handle it all.',
  },
  {
    id: 'phoneiq',
    category: 'PhoneIQ',
    icon: Phone,
    name: 'Call & SMS Tracking',
    tagline:
      'Track every inbound call to its source. Buy numbers, record calls, route intelligently.',
    monthlyPrice: null as number | null,
    annualPrice: null as number | null,
    note: 'Usage-based — pay only for what you use',
    color: '#4ade80',
    features: [
      'Local or toll-free numbers: $6 setup + $2.50/mo each',
      'Inbound calls: $0.02/min',
      'Outbound calls: $0.03/min (US)',
      'Call recording: $0.005/min',
      'SMS in/out: $0.015/message',
      'Number pools & auto-rotation',
      'UTM/campaign attribution per number',
      'IVR routing & voicemail transcription',
      'Business hours & forwarding rules',
      'Usage rolls up to your monthly invoice — no surprise charges',
    ],
    competitor:
      'CallRail: $45–$145/mo platform fee + $3/number/mo. WhatConverts: $30–$180/mo. We charge pure usage — no platform fee.',
  },
  {
    id: 'solar',
    category: 'SmartRoof',
    icon: Sun,
    name: 'Enhanced Satellite',
    tagline:
      'Upgrade the embedded widget from standard footprints to AI-powered satellite accuracy',
    monthlyPrice: 19,
    annualPrice: 15,
    note: 'Or $0.10/estimate pay-as-you-go · Included in Business',
    color: '#fb923c',
    features: [
      'AI-powered satellite measurements (vs standard footprints)',
      'Complex rooflines & multi-pitch detection',
      'Detailed roof segment analysis',
      'Up to 500 estimates/month on flat rate',
      'Pay-per-use option: $0.10/address',
      'Toggle between Standard and Enhanced modes',
      'Widget branding customization: $9/mo add-on',
      'Leads get more accurate instant estimates',
    ],
    competitor:
      'EagleView / Hover charge $5–$25 per completed measurement report. We charge $0.10 per live widget estimate.',
  },
];

const comparison = [
  {
    feature: 'Core CRM & Pipeline',
    rooflyzer: 'All plans',
    jobNimbus: '$250/mo min',
    accuLynx: '$200/mo min',
    buildertrend: '$499/mo min',
  },
  {
    feature: 'Users',
    rooflyzer: 'Per user (Business: unlimited)',
    jobNimbus: 'Per seat',
    accuLynx: 'Per seat',
    buildertrend: 'Per seat',
  },
  {
    feature: 'Measurements + AI',
    rooflyzer: 'Included',
    jobNimbus: '3rd-party tax',
    accuLynx: '3rd-party tax',
    buildertrend: 'Add-on',
  },
  {
    feature: 'Proposals & E-Sign',
    rooflyzer: 'Pro ($79/user/mo)',
    jobNimbus: 'Add-on',
    accuLynx: 'Included',
    buildertrend: 'Included',
  },
  {
    feature: 'Territory & Storm Tracking',
    rooflyzer: 'Pro ($79/user/mo)',
    jobNimbus: 'Pro plan',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'Door-to-Door Canvassing',
    rooflyzer: 'Pro ($79/user/mo)',
    jobNimbus: 'Add-on',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'Email Marketing',
    rooflyzer: '$29–$59/mo add-on',
    jobNimbus: '—',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'SMS Marketing',
    rooflyzer: '$59/mo add-on',
    jobNimbus: '—',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'Transactional Comms',
    rooflyzer: '$9–$15/mo add-on',
    jobNimbus: 'Basic only',
    accuLynx: 'Basic only',
    buildertrend: 'Basic only',
  },
  {
    feature: 'Call Tracking',
    rooflyzer: 'Usage ($0.02/min)',
    jobNimbus: '—',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'Instant Roof Calculator',
    rooflyzer: 'All plans',
    jobNimbus: '—',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'Google Reviews Automation',
    rooflyzer: 'Pro+',
    jobNimbus: '—',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'Custom Sending Domain',
    rooflyzer: 'Free with any messaging plan',
    jobNimbus: '—',
    accuLynx: '—',
    buildertrend: '—',
  },
  {
    feature: 'API Access',
    rooflyzer: 'Business ($99/user/mo)',
    jobNimbus: 'Enterprise',
    accuLynx: 'Enterprise',
    buildertrend: 'Advanced plan',
  },
  {
    feature: 'Customer Support',
    rooflyzer: 'Priority + Business concierge',
    jobNimbus: 'Standard',
    accuLynx: 'Standard',
    buildertrend: 'Standard',
  },
  {
    feature: 'Contracts',
    rooflyzer: 'Month-to-month',
    jobNimbus: 'Annual common',
    accuLynx: 'Annual common',
    buildertrend: 'Annual common',
  },
];

const faqs = [
  {
    q: 'What is actually included in each plan?',
    a: 'Every plan includes the core CRM, lead pipeline, instant estimates, invoice generation, and mobile access. Pro adds proposals with e-sign, storm tracking, territory lists, D2D canvassing, and email/SMS campaigns (sending volume through the Marketing Suite add-on). Business removes the user cap and adds AI lead scoring, advanced marketing automation, custom workflows, job costing, API access, and a dedicated account manager.',
  },
  {
    q: 'Can I cancel or change plans at any time?',
    a: 'Yes. All plans are month-to-month. You can upgrade, downgrade, or cancel from your account settings at any time. If you cancel, your data is exportable — see our Data Freedom Pledge.',
  },
  {
    q: 'Do you charge per user, per lead, or per estimate?',
    a: 'No long-term contracts. Every plan is month-to-month and includes instant estimates — no EagleView-style per-report tax. Pro and Business are priced per active user; Business has no user cap.',
  },
  {
    q: 'How does onboarding work?',
    a: 'Starter and Pro users get self-service onboarding with guided setup, import templates, and in-app help. Business customers receive white-glove onboarding with a dedicated account manager, migration assistance, and custom workflow configuration. Most contractors are tracking leads and sending estimates within the first day.',
  },
  {
    q: 'How does Rooflyzer compare to JobNimbus or AccuLynx?',
    a: 'JobNimbus and AccuLynx typically start around $200–$250/mo plus per-seat fees and third-party measurement costs. Rooflyzer Starter is free ($0/mo), Pro is $79/user/mo, and Business is $99/user/mo — month-to-month, with measurements + AI included.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes. All plans include a free trial with full access so you can run a real workflow before billing starts. No credit card required to explore during beta.',
  },
];

interface FaqItemProps {
  q: string;
  a: string;
}
/** Expand or collapse a pricing FAQ answer. */
const FaqItem = ({ q, a }: FaqItemProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`overflow-hidden cursor-pointer rounded-2xl transition-all duration-300 border ${
        open
          ? 'bg-slate-800/80 border-slate-700'
          : 'bg-white/[0.02] border-white/10 hover:border-white/20'
      }`}
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between p-5">
        <span className="text-white font-medium text-sm sm:text-base">{q}</span>
        <CaretDown
          className={`size-4 text-slate-300 flex-shrink-0 ml-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </div>
      {open && (
        <div className="px-5 pb-5 text-white/65 text-sm leading-relaxed border-t border-slate-800 pt-4">
          {a}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────

/**
 * Render plans, add-ons, and FAQs. Prices are monthly only: the annual Stripe
 * prices do not exist yet and annual checkout answers 503 until they do
 * (docs/ops/STRIPE_SEAT_ANNUAL_PRICES.md), so an annual toggle would advertise
 * terms nobody can buy. Registration already carries `interval`
 * (getPlanSignupLink), so the toggle only has to pass `true` once they exist.
 */
export const PricingPage = () => {
  const { t } = useTranslation();
  const { data: cmsData } = useSanityPage('pricing');

  const pageTitle =
    cmsData?.seo?.metaTitle ||
    'Roofing CRM Pricing — Starter $0 · Pro $79/user · Business $99/user';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'Rooflyzer OS pricing: Starter $0/mo, Pro $79/user/mo, Business $99/user/mo. Measurements + AI included, month-to-month. Everything is free during beta — founding members lock in 30% off.';
  const heroTitle = cmsData?.title || "Pricing That Doesn't Punish.";
  const heroSubtitle =
    cmsData?.description ||
    'Month-to-month plans — Starter free, Pro $79/user, Business $99/user. Instant estimates included — no per-report tax. Everything is free during beta.';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Rooflyzer OS Pricing — Starter $0 / Pro $79 per user / Business $99 per user',
    description: pageDescription,
    url: 'https://rooflyzer.pro/pricing',
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/pricing"
        keywords="roofing software pricing, roofing CRM cost, roofing management software price, roofing CRM per user"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/og/pricing.png'}
        schema={[schema]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Pricing', url: '/pricing' },
        ]}
      />

      {/* ── Value Banner ─────────────────────── */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 text-center">
        <p className="text-sm text-white/90">
          <span className="font-semibold text-emerald-400">Free during beta.</span>
          {' — '}
          Starter $0 · Pro $79/user · Business $99/user. Instant estimates included. Month-to-month.{' '}
          <a
            href="/community/founders-beta-program"
            className="underline text-slate-300 hover:text-white font-medium"
          >
            Join the founders program →
          </a>
        </p>
      </div>

      {/* ── Hero ────────────────────────────── */}
      <section className="relative pt-24 pb-12 px-4 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <SectionBadge icon={Sparkles} text="Month-to-month · Free during beta" />
          <h1 className="hero-h1 font-bold text-white mt-6 mb-5">
            {cmsData?.title ? (
              heroTitle
            ) : (
              <>
                {t('pricing.hero.titleStart', 'Pricing That ')}
                <span className="text-gradient-gold-purple">
                  {t('pricing.hero.titleHighlight', "Doesn't Punish")}
                </span>{' '}
                {t('pricing.hero.titleEnd', 'Your Growth')}
              </>
            )}
          </h1>
          <p className="hero-lead text-white/65 max-w-3xl mx-auto mb-4">{heroSubtitle}</p>
          <p className="text-sm text-white/45 max-w-xl mx-auto">
            Starter is free; Pro and Business are priced per user. No contracts or per-report
            measurement fees.
          </p>

          {/* Savings stat row */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
            {[
              { label: 'vs JobNimbus + seats', saving: '$2,400+', unit: 'saved/yr' },
              { label: 'vs AccuLynx + seats', saving: '$1,800+', unit: 'saved/yr' },
              { label: 'vs CallRail + ActiveCampaign', saving: '$2,800+', unit: 'saved/yr' },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-slate-900/60 border border-slate-800 rounded-2xl px-6 py-4 text-center"
              >
                <p className="text-white font-bold text-2xl tracking-tight">{s.saving}</p>
                <p className="text-white/55 text-xs mt-0.5">{s.unit}</p>
                <p className="text-white/30 text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Base Plan Cards ──────────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {basePlans.map((plan) => {
            const displayPrice = plan.monthlyPrice;

            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-8 flex flex-col transition-all duration-300 ${
                  plan.isPopular
                    ? 'bg-slate-900/90 border-2 border-white/20 ring-1 ring-white/10 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.8)] scale-105'
                    : 'bg-white/[0.03] border border-white/10 hover:border-white/20'
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-white text-slate-950 text-xs font-semibold uppercase tracking-wider whitespace-nowrap shadow-md">
                    {plan.highlight}
                  </div>
                )}

                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4 w-fit">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span className="text-emerald-400 text-xs font-mono font-medium">
                    Free during beta
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-white mb-1">{plan.name}</h3>
                <p className="text-white/55 text-sm mb-6 leading-relaxed">{plan.description}</p>

                <div className="mb-2">
                  <div className="flex items-end gap-2">
                    <span className="text-5xl font-bold text-white tracking-tight">
                      ${displayPrice}
                    </span>
                    <span className="text-white/40 text-sm mb-1.5">{plan.period}</span>
                  </div>
                  <p className="text-xs text-slate-400 font-medium mt-1">
                    {plan.id === 'starter' ? '1 user included' : 'Per-user pricing'} ·
                    month-to-month
                  </p>
                </div>

                <div className="h-px bg-white/10 my-5" />

                <ul className="space-y-2.5 mb-6 flex-grow">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm text-white/80">
                      <Check className="size-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {plan.missingFeatures.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm text-white/30">
                      <X className="size-4 text-white/20 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full font-semibold ${
                    plan.isPopular
                      ? 'bg-white text-slate-950 hover:bg-slate-100 shadow-md font-semibold'
                      : 'bg-slate-800 border border-slate-700 text-white hover:bg-slate-700'
                  }`}
                  onClick={() => window.location.assign(getPlanSignupLink(plan.id, false))}
                >
                  {plan.ctaText}
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-white/35 text-xs mt-8">
          <Shield className="inline size-3 mr-1 mb-0.5" />
          Month-to-month · Per-user pricing · Cancel anytime · No per-report fees
        </p>
      </section>

      {/* ── No Hidden Fees ───────────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: ListChecks,
                title: 'No setup fees',
                desc: 'Create your account, connect your domain, and start tracking leads. We do not charge implementation or onboarding fees.',
              },
              {
                icon: Users,
                title: 'Pricing for your team',
                desc: 'Starter includes one user. Pro and Business are priced per active user; Business has no user cap.',
              },
              {
                icon: Receipt,
                title: 'No per-report costs',
                desc: 'Measurements and AI are included in core pricing. No $30 per roof report. No hidden pass-through charges.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="glass-panel-interactive rounded-2xl p-6 flex flex-col"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mb-4">
                  <item.icon className="size-5 text-slate-200" />
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{item.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ROI Highlight ────────────────────── */}

      {/* ── ROI Calculator ───────────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <SectionBadge icon={TrendingUp} text="Interactive ROI" />
            <h2 className="text-3xl font-bold text-white mt-5 mb-3">
              See the revenue you are leaving on the table
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              Faster response times and lower software costs compound. Run the numbers for your
              business.
            </p>
          </div>
          <ROICalculator />
        </div>
      </section>

      {/* ── What's In Every Plan ─────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <SectionBadge icon={Star} text="Core Platform — All Plans" />
            <h2 className="text-3xl font-bold text-white mt-5 mb-3">
              {t('pricing.features.title', 'Everything Comes Standard')}
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              These core features ship on Starter, Pro, and Business. Measurements + AI are
              included; Business additionally unlocks the customer portal and removes the user cap.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 reveal-on-scroll">
            {[
              {
                icon: BarChart3,
                title: 'Lead Pipeline',
                desc: 'Full CRM with stages, source tracking, and team assignment',
              },
              {
                icon: Activity,
                title: 'Instant Roof Calculator',
                desc: 'AI-powered embed widget for your website — captures leads while you sleep',
              },
              {
                icon: Globe,
                title: 'Mobile-First Design',
                desc: 'Every screen optimized for field use on iOS and Android',
              },
              {
                icon: Users,
                title: 'Contact Management',
                desc: 'Keep homeowner details, communication, and job history in one workspace',
              },
              {
                icon: TrendingUp,
                title: 'Storm & Hail Tracking',
                desc: 'Real-time storm alerts to prioritize neighborhoods (Pro+)',
              },
              {
                icon: CircleDollarSign,
                title: 'Invoice & Payments',
                desc: 'Create, send, and track invoices with payment link support',
              },
              {
                icon: Rocket,
                title: 'D2D Canvassing',
                desc: 'GPS-enabled door-knocking with assignment and progress tracking (Pro+)',
              },
              {
                icon: Lock,
                title: 'Real-Time Security',
                desc: 'Row-level security, PKCE auth, and encrypted data at rest',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors"
              >
                <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mb-3">
                  <item.icon className="size-5 text-slate-200" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-white/55 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Add-On Modules ───────────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <SectionBadge icon={Zap} text="Add-On Modules — Bolt On Only What You Need" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-5 mb-3">
              {t('pricing.features.payForPower', 'Pay for Power,')}{' '}
              <span className="headline-gradient">
                {t('pricing.features.notBloat', 'Not Bloat')}
              </span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              {t(
                'pricing.features.modulesDesc',
                'Four focused modules you can add to any plan. Each one replaces an entire standalone SaaS tool — at a fraction of the cost.'
              )}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {addOnModules.map((mod) => (
              <div
                key={mod.id}
                className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden hover:border-white/20 transition-colors"
              >
                <div className="p-6 pb-0">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span
                        className="text-xs font-bold uppercase tracking-wider"
                        style={{ color: mod.color }}
                      >
                        {mod.category}
                      </span>
                      <div className="flex items-center gap-2 mt-1">
                        <mod.icon className="size-5" style={{ color: mod.color }} />
                        <h3 className="text-xl font-bold text-white">{mod.name}</h3>
                      </div>
                    </div>
                    <div className="text-right">
                      {mod.monthlyPrice != null ? (
                        <>
                          <div className="flex items-baseline gap-1 justify-end">
                            <span className="text-3xl font-bold text-white">
                              ${mod.monthlyPrice}
                            </span>
                            <span className="text-white/40 text-sm">/mo</span>
                          </div>
                          <p className="text-xs text-white/40 mt-0.5">{mod.note}</p>
                        </>
                      ) : (
                        <>
                          <span className="text-sm font-bold" style={{ color: mod.color }}>
                            {t('pricing.features.usageBased', 'Usage-based')}
                          </span>
                          <p className="text-xs text-white/40 mt-0.5">{mod.note}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-white/60 text-sm mb-5 leading-relaxed">{mod.tagline}</p>
                </div>

                <div className="px-6 pb-5">
                  <ul className="space-y-2">
                    {mod.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-white/75">
                        <Check
                          className="size-3.5 flex-shrink-0 mt-0.5"
                          style={{ color: mod.color }}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mx-6 mb-6 px-4 py-3 bg-white/3 rounded-xl border border-white/8">
                  <p className="text-xs text-white/40 leading-relaxed">
                    <Info className="size-3 inline mr-1 mb-0.5 opacity-60" />
                    {mod.competitor}
                  </p>
                </div>

                <div className="px-6 pb-6">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-white/15 text-white/80 hover:bg-white/10 hover:text-white text-xs"
                    onClick={() =>
                      window.location.assign(getAuthLink('/register?ref=pricing-addon'))
                    }
                  >
                    {t('pricing.features.addToPlanBeta', 'Add to my plan →')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── White-Label Email Callout ────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl p-8 sm:p-12 border border-white/10 bg-white/[0.02] relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/[0.02] rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center">
                  <Mail className="size-5 text-slate-200" />
                </div>
                <span className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                  {t('pricing.features.whiteLabelEmail', 'White-Label Email — Included Free')}
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                {t(
                  'pricing.features.sendOwnDomain',
                  "Send From Your Own Domain. We Don't Force Our Logo."
                )}
              </h2>
              <p className="text-white/70 mb-6 leading-relaxed max-w-2xl">
                {t(
                  'pricing.features.chargePrivilege',
                  "Some platforms charge $10–$50/mo for the privilege of branding your outbound emails. We think that's highway robbery."
                )}{' '}
                <code className="bg-white/10 px-1.5 py-0.5 rounded text-white text-xs">
                  {t('pricing.features.dummyDomain', 'outreach@yourcompany.com')}
                </code>{' '}
                {t(
                  'pricing.features.chargePrivilegeSuffix',
                  'instead of a generic shared domain. Not here.'
                )}
              </p>
              <p className="text-white/70 mb-8 leading-relaxed max-w-2xl">
                {t(
                  'pricing.features.messagingPlanDesc',
                  "Any account with a ConnectIQ messaging plan (Marketing Suite or Transactional) can add their custom sending domain for free — just set your DNS records and you're ready to fly."
                )}
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { step: '1', text: 'Subscribe to any ConnectIQ messaging plan' },
                  { step: '2', text: 'Add SPF, DKIM, DMARC records to your domain' },
                  { step: '3', text: 'Send from your brand address — no extra fee' },
                ].map((s) => (
                  <div key={s.step} className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-200 text-xs font-mono font-medium flex-shrink-0 mt-0.5">
                      {s.step}
                    </span>
                    <p className="text-white/70 text-sm leading-relaxed">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Competitor Comparison Table ──────── */}

      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <SectionBadge icon={TrendingUp} text="Feature-for-Feature Comparison" />
            <h2 className="text-3xl font-bold text-white mt-5 mb-3">
              {t('pricing.comparison.title', 'See Everything Side by Side')}
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              {t(
                'pricing.comparison.subtitle',
                'No cherry-picked data. This is the real feature comparison against the platforms roofers use most.'
              )}
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5 border-b border-white/10">
                  <th className="text-left px-5 py-4 text-white/60 font-medium min-w-[200px]">
                    {t('pricing.comparison.featureHeader', 'Feature')}
                  </th>
                  <th className="px-5 py-4 text-center min-w-[160px]">
                    <span className="text-white font-bold">
                      {t('pricing.comparison.rooflyzerHeader', 'Rooflyzer OS')}
                    </span>
                  </th>
                  <th className="px-5 py-4 text-center text-white/50 font-medium min-w-[130px]">
                    {t('pricing.comparison.jobnimbusHeader', 'JobNimbus')}
                  </th>
                  <th className="px-5 py-4 text-center text-white/50 font-medium min-w-[130px]">
                    {t('pricing.comparison.acculynxHeader', 'AccuLynx')}
                  </th>
                  <th className="px-5 py-4 text-center text-white/50 font-medium min-w-[130px]">
                    {t('pricing.comparison.buildertrendHeader', 'Buildertrend')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, i) => (
                  <tr
                    key={row.feature}
                    className={`border-b border-white/5 ${i % 2 === 0 ? '' : 'bg-white/2'}`}
                  >
                    <td className="px-5 py-3.5 text-white/70 font-medium">{row.feature}</td>
                    <td className="px-5 py-3.5 text-center text-emerald-400 font-medium text-xs">
                      {row.rooflyzer}
                    </td>
                    <td className="px-5 py-3.5 text-center text-white/40 text-xs">
                      {row.jobNimbus}
                    </td>
                    <td className="px-5 py-3.5 text-center text-white/40 text-xs">
                      {row.accuLynx}
                    </td>
                    <td className="px-5 py-3.5 text-center text-white/40 text-xs">
                      {row.buildertrend}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── PhoneIQ Usage Pricing ────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <SectionBadge icon={Phone} text="PhoneIQ — Transparent Usage Pricing" />
            <h2 className="text-3xl font-bold text-white mt-5 mb-3">
              {t('pricing.calls.title', 'Pay for Calls. Not for the Privilege of Making Them.')}
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              {t(
                'pricing.calls.desc',
                'CallRail charges a $45–$50/mo platform fee before you can even make a call. We charge nothing until you use it. Every charge rolls up to your next invoice — no surprise mid-month debits.'
              )}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                label: 'Phone # Setup (one-time)',
                price: '$6',
                unit: 'per number',
                sub: 'Local or toll-free',
                color: 'text-[#4ade80]',
              },
              {
                label: 'Monthly Number Rental',
                price: '$2.50',
                unit: '/number/month',
                sub: 'Accumulated on invoice',
                color: 'text-[#4ade80]',
              },
              {
                label: 'Inbound Calls (local)',
                price: '$0.02',
                unit: '/minute',
                sub: 'vs. $0.0085 raw Twilio cost',
                color: 'text-[#4ade80]',
              },
              {
                label: 'Outbound Calls (US)',
                price: '$0.03',
                unit: '/minute',
                sub: 'vs. $0.014 raw Twilio cost',
                color: 'text-[#4ade80]',
              },
              {
                label: 'Toll-Free Inbound',
                price: '$0.03',
                unit: '/minute',
                sub: 'vs. $0.022 raw Twilio cost',
                color: 'text-[#4ade80]',
              },
              {
                label: 'Call Recording',
                price: '$0.005',
                unit: '/minute',
                sub: 'Stored in your account',
                color: 'text-[#4ade80]',
              },
              {
                label: 'SMS (in or out)',
                price: '$0.015',
                unit: '/message',
                sub: 'Transactional & tracking',
                color: 'text-[#4ade80]',
              },
              {
                label: 'MMS',
                price: '$0.02',
                unit: '/message',
                sub: 'Images & media included',
                color: 'text-[#4ade80]',
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between bg-white/5 rounded-xl px-5 py-4 border border-white/10"
              >
                <div>
                  <p className="text-white font-medium text-sm">{item.label}</p>
                  <p className="text-white/40 text-xs mt-0.5">{item.sub}</p>
                </div>
                <div className="text-right">
                  <span className={`font-bold text-lg ${item.color}`}>{item.price}</span>
                  <p className="text-white/40 text-xs">{item.unit}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 rounded-xl bg-[#4ade80]/5 border border-[#4ade80]/15">
            <p className="text-[#4ade80]/80 text-sm text-center">
              <Shield className="inline size-4 mr-1 mb-0.5" />
              <strong>
                {t('pricing.calls.noPlatformFee', 'No platform fee. No monthly minimum.')}
              </strong>{' '}
              Usage is billed at the end of each month on your invoice. Track your estimate in
              real-time in the billing dashboard.
            </p>
          </div>
        </div>
      </section>

      {/* ── Plan Snapshot ────────────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl p-8 sm:p-12 border border-white/10 bg-white/[0.02] relative">
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <Crown className="size-8 text-white" />
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white">
                    Simple per-user pricing
                  </h2>
                  <p className="text-white/50 text-sm">
                    Per-user pricing · Measurements + AI included · Month-to-month · Free during
                    beta
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-5 mb-8">
                {[
                  {
                    plan: 'Starter',
                    price: '$0/mo',
                    note: 'Core OS — free during beta',
                  },
                  {
                    plan: 'Pro',
                    price: '$79/user/mo',
                    note: 'Close + field + storm',
                  },
                  {
                    plan: 'Business',
                    price: '$99/user/mo',
                    note: 'AI + multi-crew depth',
                  },
                ].map((row) => (
                  <div
                    key={row.plan}
                    className="bg-slate-900/60 rounded-2xl p-5 border border-slate-800 text-center"
                  >
                    <p className="text-white font-bold text-lg mb-2">{row.plan}</p>
                    <p className="text-white text-2xl font-bold mb-1">{row.price}</p>
                    <p className="text-slate-400 text-xs font-medium">{row.note}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button
                  size="lg"
                  className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                  onClick={() =>
                    window.location.assign(getAuthLink('/register?ref=pricing-snapshot'))
                  }
                >
                  Start Free Trial
                  <Crown className="ml-2 size-4" />
                </Button>
                <a
                  href="/community/founders-beta-program"
                  className="text-slate-300 text-sm underline hover:text-white"
                >
                  {t('pricing.claim.learnMore', 'Learn more about the founders program →')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────── */}
      <section className="pb-24 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {t('pricing.faq.title', 'Frequently Asked Questions')}
          </h2>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────── */}
      <section className="pb-24 px-4 text-center">
        <div className="max-w-2xl mx-auto bg-slate-900/80 rounded-3xl p-10 border border-slate-800 shadow-xl">
          <Rocket className="size-12 text-slate-200 mx-auto mb-5" />
          <h2 className="text-3xl font-bold text-white mb-3">
            {t('pricing.footerCTA.title', 'Ready to Replace Your Entire Stack?')}
          </h2>
          <p className="text-white/70 mb-2 leading-relaxed">
            CRM + measurements + marketing + call tracking — plans for solo roofers and growing
            teams, with no user cap on Business.
          </p>
          <p className="text-white/40 text-sm mb-8">
            Starter $0/mo · Pro $79/user/mo · Business $99/user/mo. Month-to-month.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-10 shadow-sm"
              onClick={() => window.location.assign(getAuthLink('/register?ref=pricing-cta'))}
            >
              Start Free Trial
              <ArrowRight className="ml-2 size-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => window.location.assign('/demo')}
            >
              Book a Demo
            </Button>
          </div>
          <p className="text-white/30 text-xs mt-5">
            Month-to-month · Per-user pricing · Cancel anytime
          </p>
        </div>
      </section>
    </>
  );
};
